import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { captureAPI } from '../services/api';
import { useIsAdmin } from '../identity/useIsAdmin';
import { runParse } from '../vendorImport/runParse';
import {
  FIELDS, DELIMITERS, guessMapping, unusedColumns, numericIdFields, buildImportRequest,
} from '../vendorImport/mapping';
import {
  OUTCOME_ORDER, OUTCOME_LABELS, HELD_HELP, HELD_REASONS, isHeld, fieldDiffLines, countsDiffer, restampStatus,
  serverReasonAddsInfo, importSummaryText,
} from '../vendorImport/outcomes';
import { SOURCE_MAX } from '../vendorImport/limits';
import './VendorImportPage.css';

/**
 * Vendor import (slice 2c-2, vault 2026-10-31 vendor import decisions,
 * rulings 10, 18, 21 to 27). A page, not a dialog (ruling 22), in four steps:
 *
 *   File      the file is read in the browser, in a Worker (vendorImport/).
 *   Columns   which column is which field, and the source label.
 *   Preview   the server's dry run of exactly what apply would do; link rows
 *             are chosen here (ruling 21, default none).
 *   Done      apply's own counts (apply classifies again; nothing from the
 *             preview is trusted) and the document re-stamp, followed by
 *             polling the import row.
 *
 * Import recovery (slice 2d, rulings 33 to 37): the File step lists the
 * tenant's recent imports, and an import whose document update failed or did
 * not finish can be retried there or on the Done step. Both follow the row
 * with the same polling (useImportPoll).
 *
 * Admin only, as every vendor write is; the server enforces it too.
 */
export const PAGE_SIZE = 100;
export const POLL_FAST_MS = 2000;
export const POLL_SLOW_MS = 5000;
export const POLL_SLOW_AFTER_MS = 30 * 1000;
export const POLL_STOP_MS = 5 * 60 * 1000;

export const NETWORK_APPLY_MESSAGE = 'Could not confirm the import. Run it again: rows already applied show as unchanged.';
export const POLL_GAVE_UP_MESSAGE = 'Documents are still being updated in the background. The vendors are saved; check the documents again later.';
export const COUNTS_DIFFER_MESSAGE = 'Some rows changed between the preview and the import, so these results differ from the preview.';
export const APPLY_SERVER_ERROR_MESSAGE = 'The import did not finish. Run it again: rows already applied show as unchanged.';
export const RECENT_LOAD_ERROR_MESSAGE = 'Recent imports could not be loaded.';

const STEPS = [
  { key: 'file', label: 'File' },
  { key: 'columns', label: 'Columns' },
  { key: 'preview', label: 'Preview' },
  { key: 'done', label: 'Done' },
];

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('en-US') : '0');
const rowsText = (n) => `${fmt(n)} ${n === 1 ? 'row' : 'rows'}`;

const runningMessage = (body) => {
  const since = body.running_since ? ` (started ${new Date(body.running_since).toLocaleString()})` : '';
  return `Another vendor import is running${since}. Try again when it has finished.`;
};

/** The message for a failed preview or apply. `phase` is 'preview' or 'apply'. */
export function importErrorMessage(err, phase) {
  const res = err && err.response;
  if (!res) {
    return phase === 'apply' ? NETWORK_APPLY_MESSAGE : 'Could not reach the server. Check your connection and try again.';
  }
  const body = res.data || {};
  if (res.status === 409 && body.code === 'import_running') return runningMessage(body);
  // A 413 from the body parser has no code; the import's own 413 says too_large.
  if (res.status === 413) return 'The import is too large to send. Split the file into smaller files and import each one.';
  if (res.status === 403) return 'Only tenant admins can import vendors.';
  if (res.status === 400 && body.error) return `The server did not accept the import: ${body.error}.`;
  // Apply writes row by row; whatever it wrote before the error is unchanged next time.
  if (phase === 'apply' && res.status >= 500) return APPLY_SERVER_ERROR_MESSAGE;
  return phase === 'apply' ? 'The import could not be completed. Try again.' : 'The preview could not be made. Try again.';
}

const NOT_RETRYABLE_TEXT = {
  running: 'This import is already updating its documents.',
  finished: 'The documents of this import are already updated.',
  nothing_to_do: 'This import had no documents to update.',
  import_again: 'This import did not finish. Import the file again: rows already applied show as unchanged.',
  no_scope: 'This import cannot be retried. Documents pick up the change the next time the vendor is edited.',
  changed: 'This import changed while the retry was starting. Its status is shown again.',
};

/** The message for a refused retry of an import's document update (slice 2d). */
export function retryErrorMessage(err) {
  const res = err && err.response;
  if (!res) return 'Could not reach the server. Check your connection and try again.';
  const body = res.data || {};
  if (res.status === 404) return 'This import no longer exists.';
  if (res.status === 403) return 'Only tenant admins can retry an import.';
  if (res.status === 409 && body.code === 'import_running') return runningMessage(body);
  if (res.status === 409 && body.code === 'not_retryable') return NOT_RETRYABLE_TEXT[body.reason] || 'This import cannot be retried now.';
  return 'The retry could not be started. Try again.';
}

/**
 * Follow an import row until its document update is final: every 2 s, every
 * 5 s after 30 s, stop after 5 minutes (then onGaveUp). A null id polls
 * nothing; a new nonce starts again (a retry of the same import).
 */
export function useImportPoll(importId, nonce, onRow, onGaveUp) {
  const handlers = useRef({ onRow, onGaveUp });
  handlers.current = { onRow, onGaveUp };
  useEffect(() => {
    if (!importId) return undefined;
    let cancelled = false;
    let timer = null;
    const started = Date.now();
    const tick = async () => {
      if (cancelled) return;
      try {
        const res = await captureAPI.getVendorImport(importId);
        if (cancelled) return;
        const imp = res && res.import;
        if (imp) handlers.current.onRow(imp);
        if (imp && restampStatus(imp).final) return;
      } catch (err) {
        if (cancelled) return;
        if (err && err.response && err.response.status === 404) { handlers.current.onGaveUp(importId); return; }
      }
      const elapsed = Date.now() - started;
      if (elapsed >= POLL_STOP_MS) { handlers.current.onGaveUp(importId); return; }
      timer = setTimeout(tick, elapsed >= POLL_SLOW_AFTER_MS ? POLL_SLOW_MS : POLL_FAST_MS);
    };
    timer = setTimeout(tick, POLL_FAST_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [importId, nonce]);
}

/** Retry an import's document update once at a time; answers the row or an error message. */
function useRetry() {
  const busy = useRef(false);
  const [retrying, setRetrying] = useState(null);
  const run = useCallback(async (importId) => {
    if (busy.current) return null;
    busy.current = true;
    setRetrying(importId);
    try {
      const res = await captureAPI.retryVendorImportRestamp(importId);
      return { row: res && res.import };
    } catch (err) {
      const res = err && err.response;
      // A row that changed under us is read again, so its status is current.
      let row = null;
      if (res && res.status === 409 && res.data && res.data.code === 'not_retryable') {
        try { row = (await captureAPI.getVendorImport(importId)).import; } catch { row = null; }
      }
      return { error: retryErrorMessage(err), row };
    } finally {
      busy.current = false;
      setRetrying(null);
    }
  }, []);
  return [retrying, run];
}

function StepList({ step }) {
  const at = STEPS.findIndex((s) => s.key === step);
  return (
    <ol className="vi-steps" aria-label="Import steps">
      {STEPS.map((s, i) => (
        <li key={s.key} className={i === at ? 'vi-step-current' : i < at ? 'vi-step-done' : ''} aria-current={i === at ? 'step' : undefined}>
          <span className="vi-step-number">{i + 1}</span> {s.label}
        </li>
      ))}
    </ol>
  );
}

/** A step's heading, focused when the step opens so keyboard and screen reader users land on it. */
function StepHeading({ children }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.focus(); }, []);
  return <h3 className="vi-step-heading" tabIndex={-1} ref={ref}>{children}</h3>;
}

function firstValue(parsed, col) {
  if (col === null || col === undefined) return '';
  const hit = parsed.rows.find((r) => r.cells[col]);
  return hit ? hit.cells[col] : '';
}

// ── Results table ───────────────────────────────────────────────────────────

function chipList(counts) {
  const chips = [{ key: 'all', label: 'All', count: counts.rows }];
  for (const o of OUTCOME_ORDER) {
    if (isHeld(o)) continue;
    if (counts[o] > 0) chips.push({ key: o, label: OUTCOME_LABELS[o], count: counts[o] });
  }
  if (counts.held > 0) {
    chips.push({ key: 'held', label: 'Held', count: counts.held });
    for (const r of HELD_REASONS) {
      if (counts.held_by_reason[r] > 0) chips.push({ key: r, label: OUTCOME_LABELS[r], count: counts.held_by_reason[r], held: true });
    }
  }
  return chips;
}

const matchesFilter = (row, filter) => filter === 'all' || row.outcome === filter || (filter === 'held' && isHeld(row.outcome));

function RowDetails({ r }) {
  const lines = [];
  if (r.outcome === 'name_taken') {
    lines.push(
      <span key="owner">
        {'Belongs to '}
        {r.owner && r.owner.vendor_id
          ? <Link to={`/vendor-directory?vendor=${r.owner.vendor_id}`}>{r.owner.display_name || 'another vendor'}</Link>
          : 'another vendor'}
        .
      </span>,
    );
  } else if (isHeld(r.outcome)) {
    lines.push(<span key="help">{HELD_HELP[r.outcome]}</span>);
  }
  if (isHeld(r.outcome) && serverReasonAddsInfo(r.outcome, r.reason)) lines.push(<span key="reason" className="vi-muted">{`Server: ${r.reason}`}</span>);
  if (r.outcome === 'link' || r.outcome === 'link_skipped') {
    lines.push(
      <span key="link">
        {'Matches '}
        {r.vendor_id ? <Link to={`/vendor-directory?vendor=${r.vendor_id}`}>a vendor you created by hand</Link> : 'a vendor you created by hand'}
        {r.outcome === 'link_skipped' ? '. Not linked.' : '.'}
      </span>,
    );
  }
  if (r.outcome === 'merged') lines.push(<span key="merged">{`Merged into row ${r.merged_into}.`}</span>);
  if (r.outcome === 'duplicate') lines.push(<span key="dup">{`Same as row ${r.duplicate_of}.`}</span>);
  if (r.merged_rows && r.merged_rows.length > 0) {
    lines.push(<span key="mrows">{`Also takes the names on ${r.merged_rows.length === 1 ? 'row' : 'rows'} ${r.merged_rows.join(', ')}.`}</span>);
  }
  for (const [i, l] of fieldDiffLines(r.fields).entries()) lines.push(<span key={`f${i}`}>{l}</span>);
  if (r.names_added && r.names_added.length > 0 && r.outcome !== 'create') {
    lines.push(<span key="names">{`Adds printed names: ${r.names_added.join(', ')}`}</span>);
  }
  return <div className="vi-details">{lines}</div>;
}

export function ResultTable({ rows, counts, initialFilter = 'all', linkMode, linkRows, onToggleLink }) {
  const [filter, setFilter] = useState(initialFilter);
  const [page, setPage] = useState(0);
  const shown = useMemo(() => rows.filter((r) => matchesFilter(r, filter)), [rows, filter]);
  const pages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const at = Math.min(page, pages - 1);
  const slice = shown.slice(at * PAGE_SIZE, at * PAGE_SIZE + PAGE_SIZE);
  const choose = linkMode === 'rows';

  return (
    <div className="vi-results">
      <div className="vi-chips" role="group" aria-label="Show rows by result">
        {chipList(counts).map((c) => (
          <button
            key={c.key}
            type="button"
            className={['vi-chip', c.held ? 'vi-chip-held' : '', c.key === 'held' ? 'vi-chip-held-total' : '', filter === c.key ? 'vi-chip-active' : ''].filter(Boolean).join(' ')}
            aria-pressed={filter === c.key}
            onClick={() => { setFilter(c.key); setPage(0); }}
          >
            {c.label} <span className="vi-chip-count">{fmt(c.count)}</span>
          </button>
        ))}
      </div>
      <table className="vi-table">
        <thead>
          <tr>
            <th className="vi-th-row">Row</th>
            <th>Result</th>
            <th>Display name</th>
            <th>Vendor number</th>
            <th>Printed names</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          {slice.map((r) => (
            <tr key={r.row} data-testid={`vi-row-${r.row}`} className={isHeld(r.outcome) ? 'vi-row-held' : ''}>
              <td className="vi-td-row">{r.row}</td>
              <td>
                <span className={`vi-outcome vi-outcome-${isHeld(r.outcome) ? 'held' : r.outcome}`}>{OUTCOME_LABELS[r.outcome] || r.outcome}</span>
                {choose && r.outcome === 'link' && (
                  <label className="vi-link-choice">
                    <input
                      type="checkbox"
                      checked={linkRows.has(r.row)}
                      onChange={() => onToggleLink(r.row)}
                      aria-label={`Link row ${r.row}`}
                    />
                    Link
                  </label>
                )}
              </td>
              <td>{r.display_name || ''}</td>
              <td>{r.vendor_number || ''}</td>
              <td>{(r.names || []).join(', ')}</td>
              <td><RowDetails r={r} /></td>
            </tr>
          ))}
          {slice.length === 0 && (
            <tr><td colSpan={6} className="vi-no-rows">No rows with this result.</td></tr>
          )}
        </tbody>
      </table>
      {shown.length > PAGE_SIZE && (
        <div className="vi-pager">
          <button type="button" className="btn btn-sm" disabled={at === 0} onClick={() => setPage(at - 1)}>Previous</button>
          <span className="vi-pager-text">
            {`Rows ${fmt(at * PAGE_SIZE + 1)} to ${fmt(Math.min(shown.length, (at + 1) * PAGE_SIZE))} of ${fmt(shown.length)}`}
          </span>
          <button type="button" className="btn btn-sm" disabled={at >= pages - 1} onClick={() => setPage(at + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}

// ── Steps ───────────────────────────────────────────────────────────────────

function FileStep({ parsing, error, onChoose }) {
  const input = useRef(null);
  return (
    <section className="vi-panel">
      <StepHeading>Choose a file</StepHeading>
      <p>
        A CSV file or an Excel workbook (.xlsx, first sheet only) with a header row and one vendor per row. Up to
        2 MB and 5,000 vendors. The file is read in your browser; nothing is saved until you import.
      </p>
      <input
        ref={input}
        type="file"
        className="vi-file-input"
        accept=".csv,.txt,.tsv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        aria-label="Vendor file"
        data-testid="vi-file-input"
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          e.target.value = '';
          if (f) onChoose(f);
        }}
      />
      <div className="vi-actions">
        <button type="button" className="btn btn-primary" disabled={parsing} onClick={() => input.current && input.current.click()}>
          {parsing ? 'Reading file...' : 'Choose file'}
        </button>
        <Link to="/vendor-directory" className="btn">Cancel</Link>
      </div>
      {parsing && <p className="vi-muted" role="status">Reading the file...</p>}
      {error && <p className="vi-error" role="alert">{error}</p>}
    </section>
  );
}

const statusText = (imp) => (imp.status === 'applying' && imp.restamp && imp.restamp.state === 'writing'
  ? 'Saving vendors...'
  : restampStatus(imp).text);

/** The tenant's recent imports, on the File step (slice 2d). Never blocks an import. */
export function RecentImports() {
  const [rows, setRows] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [messages, setMessages] = useState({});
  const [poll, setPoll] = useState(null);
  const [gaveUp, setGaveUp] = useState(null);
  const [retrying, runRetry] = useRetry();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await captureAPI.listVendorImports();
        if (!cancelled) setRows((res && res.imports) || []);
      } catch {
        if (!cancelled) setLoadError(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const replace = useCallback((imp) => setRows((prev) => (prev ? prev.map((r) => (r.id === imp.id ? imp : r)) : prev)), []);
  useImportPoll(poll && poll.id, poll && poll.nonce, replace, setGaveUp);

  const retry = async (id) => {
    setMessages((m) => ({ ...m, [id]: null }));
    const out = await runRetry(id);
    if (!out) return;
    if (out.row) replace(out.row);
    if (out.error) { setMessages((m) => ({ ...m, [id]: out.error })); return; }
    setGaveUp(null);
    setPoll((p) => ({ id, nonce: (p ? p.nonce : 0) + 1 }));
  };

  if (loadError) return <p className="vi-muted vi-recent-error">{RECENT_LOAD_ERROR_MESSAGE}</p>;
  if (!rows || rows.length === 0) return null;
  return (
    <section className="vi-panel vi-recent" aria-labelledby="vi-recent-heading" data-testid="vi-recent">
      <h3 id="vi-recent-heading" className="vi-recent-heading">Recent imports</h3>
      <table className="vi-table vi-recent-table">
        <thead>
          <tr><th>Started</th><th>File</th><th>Source</th><th>Rows</th><th>Documents</th><th aria-label="Actions" /></tr>
        </thead>
        <tbody>
          {rows.map((imp) => {
            const st = restampStatus(imp);
            const text = gaveUp === imp.id && !st.final ? POLL_GAVE_UP_MESSAGE : statusText(imp);
            return (
              <tr key={imp.id} data-testid={`vi-recent-${imp.id}`}>
                <td>{imp.created_at ? new Date(imp.created_at).toLocaleString() : ''}</td>
                <td>{imp.file_name || 'No file name'}</td>
                <td>{imp.external_source}</td>
                <td>{importSummaryText(imp)}</td>
                <td>
                  <span className={`vi-restamp-text vi-restamp-${st.tone}`}>{text}</span>
                  {messages[imp.id] && <span className="vi-error vi-recent-message" role="alert">{messages[imp.id]}</span>}
                </td>
                <td>
                  {st.final && st.retryable && (
                    <button type="button" className="btn btn-sm" disabled={retrying !== null} onClick={() => retry(imp.id)}>
                      {retrying === imp.id ? 'Retrying...' : 'Retry document update'}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function ColumnsStep({ file, parsed, delimiter, onDelimiter, mapping, setMapping, source, setSource, previewing, error, onBack, onPreview }) {
  const unused = unusedColumns(parsed.header, mapping);
  const numeric = numericIdFields(parsed, mapping);
  return (
    <section className="vi-panel">
      <StepHeading>Match the columns</StepHeading>
      <p className="vi-summary">
        {`${file.name}: ${fmt(parsed.rows.length)} vendor ${parsed.rows.length === 1 ? 'row' : 'rows'}, ${parsed.header.length} ${parsed.header.length === 1 ? 'column' : 'columns'}.`}
        {parsed.kind === 'xlsx' && ' First sheet only.'}
        {parsed.encoding === 'windows-1252' && ' The file is not UTF-8, so it was read as Windows-1252.'}
        {(parsed.encoding === 'utf-16le' || parsed.encoding === 'utf-16be') && ' Read as UTF-16.'}
      </p>
      {parsed.kind === 'csv' && (
        <div className="vi-field">
          <label htmlFor="vi-delimiter">Separator</label>
          <select id="vi-delimiter" value={delimiter} onChange={(e) => onDelimiter(e.target.value)}>
            {DELIMITERS.map((d) => (
              <option key={d.label} value={d.value}>
                {d.value === '' ? `Detect (found ${DELIMITERS.find((x) => x.value === parsed.delimiter)?.label.toLowerCase() || 'comma'})` : d.label}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="vi-mapping">
        {FIELDS.map((f) => {
          const col = mapping[f.key];
          const sample = firstValue(parsed, col);
          return (
            <div className="vi-field" key={f.key}>
              <label htmlFor={`vi-map-${f.key}`}>{f.label}{f.required ? ' (required)' : ''}</label>
              <select
                id={`vi-map-${f.key}`}
                value={col === null || col === undefined ? '' : String(col)}
                onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value === '' ? null : Number(e.target.value) })}
                aria-describedby={`vi-map-${f.key}-hint`}
              >
                <option value="">{f.required ? 'Choose a column' : 'Not used'}</option>
                {parsed.header.map((h, i) => <option key={h} value={String(i)}>{h}</option>)}
              </select>
              <span className="vi-hint" id={`vi-map-${f.key}-hint`}>
                {f.hint}
                {sample && <span className="vi-sample">{` First value: ${sample}`}</span>}
              </span>
            </div>
          );
        })}
      </div>
      {numeric.map((f) => (
        <p key={f.key} className="vi-warning" role="note">
          {`${f.label}: some values in this column are stored as numbers in the workbook, so leading zeros (as in 00123) are lost. To keep them, format the column as Text in Excel and save the file again.`}
        </p>
      ))}
      {unused.length > 0 && (
        <p className="vi-muted">{`${unused.length} ${unused.length === 1 ? 'column' : 'columns'} not used: ${unused.join(', ')}`}</p>
      )}
      <div className="vi-field">
        <label htmlFor="vi-source">Source label</label>
        <input
          id="vi-source"
          type="text"
          value={source}
          maxLength={SOURCE_MAX}
          onChange={(e) => setSource(e.target.value)}
          aria-describedby="vi-source-hint"
        />
        <span className="vi-hint" id="vi-source-hint">
          The system this file comes from, such as the name of your ERP. Use the same label every time: ids only
          match within one label.
        </span>
      </div>
      {error && <p className="vi-error" role="alert">{error}</p>}
      <div className="vi-actions">
        <button type="button" className="btn" disabled={previewing} onClick={onBack}>Back</button>
        <button type="button" className="btn btn-primary" disabled={previewing} onClick={onPreview}>
          {previewing ? 'Checking...' : 'Preview import'}
        </button>
      </div>
    </section>
  );
}

function PreviewNotes({ preview }) {
  const repeated = preview.warnings?.repeated_display_names || [];
  const un = preview.unmatched_names;
  return (
    <>
      {repeated.length > 0 && (
        <div className="vi-warning" role="note">
          Display names used by more than one row. They are imported as separate vendors:
          <ul>
            {repeated.map((w) => <li key={w.display_name}>{`${w.display_name} (rows ${w.rows.join(', ')})`}</li>)}
          </ul>
        </div>
      )}
      {un && un.total > 0 && (
        <details className="vi-unmatched" data-testid="vi-unmatched">
          <summary>{`Printed names on your documents that no vendor matches (${fmt(un.total)})`}</summary>
          <p className="vi-muted">
            These names appear on documents but match no vendor record and no row of this file. Add them as printed
            names in the file, or in the Vendor Directory after the import.
          </p>
          <ul>
            {un.names.map((n) => <li key={n.key}>{`${n.printed_name} (${fmt(n.documents)} ${n.documents === 1 ? 'document' : 'documents'})`}</li>)}
          </ul>
          {un.truncated && <p className="vi-muted">{`Showing the ${un.names.length} most frequent.`}</p>}
          {un.note && <p className="vi-muted">{un.note}</p>}
        </details>
      )}
    </>
  );
}

function LinkControl({ count, mode, setMode, chosen }) {
  return (
    <fieldset className="vi-link-mode">
      <legend>{`${rowsText(count)} ${count === 1 ? 'matches' : 'match'} a vendor you created by hand`}</legend>
      <p className="vi-hint">
        Linking gives that vendor this file&apos;s id, so later imports update it. Values you typed are kept; the
        file only fills empty ones.
      </p>
      {[
        { value: 'none', label: 'Do not link' },
        { value: 'all', label: 'Link all' },
        { value: 'rows', label: `Choose rows${mode === 'rows' ? ` (${chosen} chosen)` : ''}` },
      ].map((o) => (
        <label key={o.value} className="vi-radio">
          <input type="radio" name="vi-link-mode" value={o.value} checked={mode === o.value} onChange={() => setMode(o.value)} />
          {o.label}
        </label>
      ))}
    </fieldset>
  );
}

function PreviewStep({ preview, linkMode, setLinkMode, linkRows, onToggleLink, applying, error, onBack, onApply }) {
  const c = preview.counts;
  return (
    <section className="vi-panel">
      <StepHeading>Preview</StepHeading>
      <p>
        Nothing has been saved yet. This is what importing the file would do.
        {c.held > 0 && ` ${rowsText(c.held)} ${c.held === 1 ? 'is' : 'are'} held and will not be imported; fix them in the file and import it again.`}
      </p>
      <PreviewNotes preview={preview} />
      {c.link > 0 && <LinkControl count={c.link} mode={linkMode} setMode={setLinkMode} chosen={linkRows.size} />}
      <ResultTable rows={preview.rows} counts={c} linkMode={linkMode} linkRows={linkRows} onToggleLink={onToggleLink} />
      {error && <p className="vi-error" role="alert">{error}</p>}
      <div className="vi-actions">
        <button type="button" className="btn" disabled={applying} onClick={onBack}>Back</button>
        <button type="button" className="btn btn-primary" disabled={applying} aria-busy={applying} onClick={onApply}>
          {applying ? 'Importing...' : 'Import vendors'}
        </button>
      </div>
    </section>
  );
}

const SUMMARY = [
  ['create', 'New vendors'], ['update', 'Updated'], ['link', 'Linked'], ['kept', 'Your values kept'],
  ['unchanged', 'Unchanged'], ['merged', 'Merged into another row'], ['duplicate', 'Repeated rows'],
  ['link_skipped', 'Not linked'], ['held', 'Held'],
];

function DoneStep({ result, previewCounts, importRow, pollGaveUp, onAgain, retrying, retryError, onRetry }) {
  const c = result.counts;
  const status = result.restamp.state === 'none'
    ? restampStatus({ status: 'applied', restamp: { state: 'none' } })
    : restampStatus(importRow || { status: 'applying', restamp: result.restamp });
  return (
    <section className="vi-panel">
      <StepHeading>Import finished</StepHeading>
      <ul className="vi-done-counts">
        {SUMMARY.filter(([k]) => c[k] > 0).map(([k, label]) => (
          <li key={k} className={k === 'held' ? 'vi-done-held' : ''}><strong>{fmt(c[k])}</strong> {label}</li>
        ))}
      </ul>
      {countsDiffer(previewCounts, c) && <p className="vi-warning" role="note">{COUNTS_DIFFER_MESSAGE}</p>}
      <p className={`vi-restamp vi-restamp-${pollGaveUp && !status.final ? 'warn' : status.tone}`} role="status">
        {pollGaveUp && !status.final ? POLL_GAVE_UP_MESSAGE : status.text}
      </p>
      {status.final && status.retryable && (
        <div className="vi-actions vi-retry">
          <button type="button" className="btn" disabled={retrying} aria-busy={retrying} onClick={onRetry}>
            {retrying ? 'Retrying...' : 'Retry document update'}
          </button>
        </div>
      )}
      {retryError && <p className="vi-error" role="alert">{retryError}</p>}
      <ResultTable rows={result.rows} counts={c} initialFilter={c.held > 0 ? 'held' : 'all'} />
      <div className="vi-actions">
        <Link to="/vendor-directory" className="btn btn-primary">Back to Vendor Directory</Link>
        <button type="button" className="btn" onClick={onAgain}>Import another file</button>
      </div>
    </section>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export default function VendorImportPage() {
  const isAdmin = useIsAdmin();
  const [step, setStep] = useState('file');
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [delimiter, setDelimiter] = useState('');
  const [mapping, setMapping] = useState(null);
  const [source, setSource] = useState('import');
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [linkMode, setLinkMode] = useState('none');
  const [linkRows, setLinkRows] = useState(() => new Set());
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [result, setResult] = useState(null);
  const [importRow, setImportRow] = useState(null);
  const [pollGaveUp, setPollGaveUp] = useState(false);
  const [donePoll, setDonePoll] = useState(null);
  const [retryError, setRetryError] = useState(null);
  const [retrying, runRetry] = useRetry();
  const applyingRef = useRef(false);
  const parseSeq = useRef(0);
  const parsedRef = useRef(null);

  /**
   * Read the file. Only the latest parse is kept: an earlier one that answers
   * later is dropped. With keepMapping (a separator change) a manual mapping
   * survives when the header comes back the same.
   */
  const readFile = useCallback(async (f, delim, { keepMapping = false } = {}) => {
    const seq = ++parseSeq.current;
    setParsing(true);
    setParseError(null);
    const out = await runParse(f, { delimiter: delim });
    if (seq !== parseSeq.current) return false;
    setParsing(false);
    if (!out || !out.ok) {
      setParseError((out && out.error) || 'Could not read this file.');
      return false;
    }
    const before = parsedRef.current;
    const sameHeader = before && before.header.length === out.header.length && before.header.every((h, i) => h === out.header[i]);
    parsedRef.current = out;
    setParsed(out);
    if (!(keepMapping && sameHeader)) setMapping(guessMapping(out.header));
    return true;
  }, []);

  const chooseFile = async (f) => {
    setFile(f);
    setDelimiter('');
    parsedRef.current = null;
    if (await readFile(f, '')) setStep('columns');
  };

  const changeDelimiter = async (d) => {
    setDelimiter(d);
    await readFile(file, d, { keepMapping: true });
  };

  const requestBody = (link) => buildImportRequest({ parsed, mapping, source, fileName: file && file.name, link });

  const runPreview = async () => {
    const built = requestBody(null);
    if (built.error) { setPreviewError(built.error); return; }
    setPreviewing(true);
    setPreviewError(null);
    try {
      const res = await captureAPI.previewVendorImport(built.body);
      setPreview(res);
      setLinkMode('none');
      setLinkRows(new Set());
      setApplyError(null);
      setStep('preview');
    } catch (err) {
      setPreviewError(importErrorMessage(err, 'preview'));
    } finally {
      setPreviewing(false);
    }
  };

  const toggleLink = (row) => setLinkRows((prev) => {
    const next = new Set(prev);
    if (next.has(row)) next.delete(row); else next.add(row);
    return next;
  });

  const runApply = async () => {
    if (applyingRef.current) return;
    const link = { mode: linkMode, rows: linkMode === 'rows' ? [...linkRows].sort((a, b) => a - b) : [] };
    const built = requestBody(link);
    if (built.error) { setApplyError(built.error); return; }
    applyingRef.current = true;
    setApplying(true);
    setApplyError(null);
    try {
      const res = await captureAPI.applyVendorImport(built.body);
      setResult(res);
      setImportRow(null);
      setPollGaveUp(false);
      setRetryError(null);
      setDonePoll(res && res.restamp && res.restamp.state === 'pending' ? { id: res.import_id, nonce: 0 } : null);
      setStep('done');
    } catch (err) {
      setApplyError(importErrorMessage(err, 'apply'));
    } finally {
      applyingRef.current = false;
      setApplying(false);
    }
  };

  // Follow the re-stamp, and a retry of it, with the shared poll.
  useImportPoll(step === 'done' && donePoll ? donePoll.id : null, donePoll && donePoll.nonce, setImportRow, () => setPollGaveUp(true));

  const retryDone = async () => {
    setRetryError(null);
    const out = await runRetry(result.import_id);
    if (!out) return;
    if (out.row) setImportRow(out.row);
    if (out.error) { setRetryError(out.error); return; }
    setPollGaveUp(false);
    setDonePoll((p) => ({ id: result.import_id, nonce: (p ? p.nonce : 0) + 1 }));
  };

  const startAgain = () => {
    parseSeq.current += 1;
    parsedRef.current = null;
    setDonePoll(null);
    setRetryError(null);
    setStep('file');
    setFile(null);
    setParsed(null);
    setMapping(null);
    setPreview(null);
    setResult(null);
    setImportRow(null);
    setParseError(null);
    setPreviewError(null);
    setApplyError(null);
  };

  if (!isAdmin) {
    return (
      <div className="vi-page">
        <div className="vi-page-header"><h2>Import vendors</h2></div>
        <p className="vi-muted">Only tenant admins can import vendors.</p>
        <Link to="/vendor-directory">Back to Vendor Directory</Link>
      </div>
    );
  }

  return (
    <div className="vi-page">
      <div className="vi-page-header">
        <Link to="/vendor-directory" className="vi-back">Vendor Directory</Link>
        <h2>Import vendors</h2>
        <p className="vi-page-subtitle">
          Add or update vendor records from your own list. Vendors not in the file are left as they are, and values
          you typed by hand are never replaced.
        </p>
      </div>
      <StepList step={step} />
      {step === 'file' && (
        <>
          <FileStep parsing={parsing} error={parseError} onChoose={chooseFile} />
          <RecentImports />
        </>
      )}
      {step === 'columns' && parsed && (
        <ColumnsStep
          file={file}
          parsed={parsed}
          delimiter={delimiter}
          onDelimiter={changeDelimiter}
          mapping={mapping}
          setMapping={setMapping}
          source={source}
          setSource={setSource}
          previewing={previewing || parsing}
          error={previewError || parseError}
          onBack={() => { setStep('file'); setPreviewError(null); }}
          onPreview={runPreview}
        />
      )}
      {step === 'preview' && preview && (
        <PreviewStep
          preview={preview}
          linkMode={linkMode}
          setLinkMode={setLinkMode}
          linkRows={linkRows}
          onToggleLink={toggleLink}
          applying={applying}
          error={applyError}
          onBack={() => { setStep('columns'); setApplyError(null); }}
          onApply={runApply}
        />
      )}
      {step === 'done' && result && (
        <DoneStep
          result={result}
          previewCounts={preview && preview.counts}
          importRow={importRow}
          pollGaveUp={pollGaveUp}
          onAgain={startAgain}
          retrying={retrying !== null}
          retryError={retryError}
          onRetry={retryDone}
        />
      )}
    </div>
  );
}
